import { sumBy } from 'lodash'
import moment from 'moment'

import { httpHelper } from '@/utils'

export const MAX_AVG_WEEKLY_WORKING = 48
export const MAX_BIWEEKLY_DRIVING = 90
export const MAX_WEEKLY_DRIVING = 56
export const MAX_WEEKLY_WORKING = 60
export const ECOSCORE_LOOKBACK_DAYS = 30

const state = {
    ecoscoreArv: [],
    ecoscoreDrivers: [],
    ecoscoreTrucks: [],
    isEcoscoreArvLoading: false,
    isEcoscoreDriversLoading: false,
    isEcoscoreTrucksLoading: false,
}

const getters = {
    ecoscoreArvByDays: state => numDays => {
        return state.ecoscoreArv
            .map(driver => {
                const entries = driver.dailyScores.slice(0, numDays)
                const breakTime = sumBy(entries, entry => entry.break_time || 0)
                const drivingTime = sumBy(
                    entries,
                    entry => entry.driving_time || 0
                )
                const workingTime = sumBy(
                    entries,
                    entry => entry.working_time || 0
                )

                const weeklyDrivingMap = new Map()
                const weeklyWorkingMap = new Map()
                for (const entry of entries) {
                    const week = moment(entry.date).format('GGGG-[W]WW')
                    weeklyDrivingMap.set(
                        week,
                        (weeklyDrivingMap.get(week) || 0) +
                            (entry.driving_time || 0)
                    )
                    weeklyWorkingMap.set(
                        week,
                        (weeklyWorkingMap.get(week) || 0) +
                            (entry.working_time || 0) +
                            (entry.driving_time || 0)
                    )
                }

                const weekKeys = [
                    ...new Set([
                        ...weeklyDrivingMap.keys(),
                        ...weeklyWorkingMap.keys(),
                    ]),
                ].sort()

                const recentWeekKeys = weekKeys.slice(-26)
                const numWeeks = Math.max(1, recentWeekKeys.length)
                const recentWeeklyWorkingTotal = sumBy(
                    recentWeekKeys,
                    week => weeklyWorkingMap.get(week) || 0
                )
                const avgWeeklyWorking =
                    Math.round(
                        (recentWeeklyWorkingTotal / numWeeks / 60) * 10
                    ) / 10

                const maxWeeklyWorking =
                    Math.round(
                        (Math.max(0, ...weeklyWorkingMap.values()) / 60) * 10
                    ) / 10

                const maxWeeklyDriving =
                    Math.round(
                        (Math.max(0, ...weeklyDrivingMap.values()) / 60) * 10
                    ) / 10

                let maxBiweeklyDriving = 0
                weekKeys.forEach((week, i) => {
                    if (i) {
                        maxBiweeklyDriving = Math.max(
                            maxBiweeklyDriving,
                            (weeklyDrivingMap.get(week) || 0) +
                                (weeklyDrivingMap.get(weekKeys[i - 1]) || 0)
                        )
                    }
                })
                maxBiweeklyDriving =
                    Math.round((maxBiweeklyDriving / 60) * 10) / 10

                const hasViolation =
                    avgWeeklyWorking > MAX_AVG_WEEKLY_WORKING ||
                    maxWeeklyWorking > MAX_WEEKLY_WORKING ||
                    maxWeeklyDriving > MAX_WEEKLY_DRIVING ||
                    maxBiweeklyDriving > MAX_BIWEEKLY_DRIVING

                return {
                    id: driver.id,
                    name: driver.name,
                    driving: drivingTime,
                    break: breakTime,
                    working: workingTime,
                    total: drivingTime + breakTime + workingTime,
                    avgWeeklyWorking,
                    maxWeeklyWorking,
                    maxWeeklyDriving,
                    maxBiweeklyDriving,
                    hasViolation,
                }
            })
            .sort((a, b) => b.total - a.total)
    },
    ecoscoreArvMonthlyData: state => {
        const months = []
        for (let offset = 5; offset >= 0; offset--) {
            const monthStart = moment()
                .subtract(offset, 'months')
                .startOf('month')
            const monthEnd = monthStart.clone().endOf('month')
            let totalDriving = 0
            let totalWorking = 0
            for (const driver of state.ecoscoreArv) {
                for (const day of driver.dailyScores) {
                    if (
                        moment(day.date).isBetween(
                            monthStart,
                            monthEnd,
                            'day',
                            '[]'
                        )
                    ) {
                        totalDriving += day.driving_time || 0
                        totalWorking +=
                            (day.working_time || 0) + (day.driving_time || 0)
                    }
                }
            }
            months.push({
                label: monthStart.format('MMM YY'),
                driving: Math.round(totalDriving / 60),
                working: Math.round(totalWorking / 60),
            })
        }
        return months
    },
    ecoscoreDriversByDays: state => numDays => {
        return state.ecoscoreDrivers
            .map(driver => {
                const dailyScores = driver.dailyScores.slice(0, numDays)
                if (!dailyScores.length) {
                    return null
                }

                const sum = key =>
                    sumBy(dailyScores, entry => entry.scores[key] || 0)
                const avg = key => {
                    const valid = dailyScores.filter(
                        item => item.scores[key] !== null
                    )
                    return valid.length
                        ? Math.round(
                              (sumBy(valid, item => Number(item.scores[key])) /
                                  valid.length) *
                                  100
                          ) / 100
                        : null
                }

                const totalDistance = sum('total_distance')
                const totalFuel = sum('total_fuel')
                const totalScore = avg('total_score')

                const scoreValues = dailyScores
                    .map(entry => entry.scores.total_score)
                    .filter(v => v !== null)
                const mid = Math.floor(scoreValues.length / 2)
                const firstAvg = mid
                    ? scoreValues.slice(0, mid).reduce((a, b) => a + b, 0) / mid
                    : 0
                const secondLen = scoreValues.length - mid
                const secondAvg = secondLen
                    ? scoreValues.slice(mid).reduce((a, b) => a + b, 0) /
                      secondLen
                    : 0

                return {
                    id: driver.id,
                    name: driver.name,
                    averageScores: {
                        idle_fuel_score: avg('idle_fuel_score'),
                        fuel_consumption_score: avg('fuel_consumption_score'),
                        harsh_acceleration_score: avg(
                            'harsh_acceleration_score'
                        ),
                        harsh_braking_score: avg('harsh_braking_score'),
                        harsh_cornering_score: avg('harsh_cornering_score'),
                        cruise_score: avg('cruise_score'),
                        overspeeding_score: avg('overspeeding_score'),
                        overrev_score: avg('overrev_score'),
                        brake_feathering_score: avg('brake_feathering_score'),
                        retarder_score: avg('retarder_score'),
                    },
                    entries: dailyScores.map(entry => ({
                        date: entry.date,
                        ...entry.scores,
                    })),
                    totalDistance: Math.round(totalDistance),
                    avgConsumption:
                        totalDistance > 0
                            ? Math.round((totalFuel / totalDistance) * 1000) /
                              10
                            : 0,
                    avgScore:
                        totalScore !== null
                            ? Math.round(totalScore * 10) / 10
                            : 0,
                    avgSpeed: Math.round(
                        sum('average_speed') / dailyScores.length || 0
                    ),
                    trendDiff: Math.round((secondAvg - firstAvg) * 10) / 10,
                    drivingTime: sum('driving_time'),
                    breakTime: sum('break_time'),
                    workingTime: sum('working_time'),
                }
            })
            .filter(Boolean)
    },
    ecoscoreTrucksByDays: state => numDays => {
        return state.ecoscoreTrucks
            .map(truck => {
                const dailyScores = truck.dailyScores.slice(0, numDays)
                if (!dailyScores.length) {
                    return null
                }

                const totalDistance = sumBy(
                    dailyScores,
                    entry => entry.scores.total_distance || 0
                )
                const totalFuel = sumBy(
                    dailyScores,
                    entry => entry.scores.total_fuel || 0
                )

                return {
                    id: truck.id,
                    name: truck.name,
                    totalFuel: Math.round(totalFuel),
                    totalDistance: Math.round(totalDistance),
                    avgConsumption: totalDistance
                        ? Math.round((totalFuel / totalDistance) * 1000) / 10
                        : 0,
                    entries: dailyScores.map(entry => ({
                        date: entry.date,
                        total_fuel: entry.scores.total_fuel,
                        total_distance: entry.scores.total_distance,
                    })),
                }
            })
            .filter(Boolean)
    },
}

const actions = {
    async loadEcoscoreArv({ commit }) {
        commit('setEcoscoreArvLoading', true)
        try {
            const startDate = moment()
                .subtract(ECOSCORE_LOOKBACK_DAYS, 'days')
                .format('YYYY-MM-DD')
            const today = moment().format('YYYY-MM-DD')
            const { data } = await httpHelper.getRecursively(
                '/ecoscore/arv-ecoscore/',
                {
                    params: {
                        limit: process.env.VUE_APP_LIMIT_RECORDS_PER_REQUEST,
                        start_date: startDate,
                        end_date: today,
                    },
                }
            )
            commit(
                'setEcoscoreArv',
                data.results.map(item => ({
                    id: item.driver_details.id,
                    name: item.driver_details.name,
                    dailyScores: item.daily_scores
                        .filter(
                            day => day.date >= startDate && day.date <= today
                        )
                        .slice()
                        .sort((a, b) => b.date.localeCompare(a.date))
                        .map(day => ({
                            date: day.date,
                            driving_time: day.scores.driving_time,
                            break_time: day.scores.break_time,
                            working_time: day.scores.working_time,
                        })),
                }))
            )
        } catch {
            commit('setEcoscoreArv', [])
        } finally {
            commit('setEcoscoreArvLoading', false)
        }
    },
    async loadEcoscoreDrivers({ commit }) {
        commit('setEcoscoreDriversLoading', true)
        try {
            const startDate = moment()
                .subtract(ECOSCORE_LOOKBACK_DAYS, 'days')
                .format('YYYY-MM-DD')
            const today = moment().format('YYYY-MM-DD')
            const { data } = await httpHelper.getRecursively(
                '/ecoscore/driver-ecoscore/',
                {
                    params: {
                        limit: process.env.VUE_APP_LIMIT_RECORDS_PER_REQUEST,
                        start_date: startDate,
                        end_date: today,
                    },
                }
            )
            commit(
                'setEcoscoreDrivers',
                data.results.map(item => ({
                    id: item.driver_details.id,
                    name: item.driver_details.name,
                    dailyScores: item.daily_scores
                        .filter(
                            day => day.date >= startDate && day.date <= today
                        )
                        .slice()
                        .sort((a, b) => b.date.localeCompare(a.date)),
                }))
            )
        } catch {
            commit('setEcoscoreDrivers', [])
        } finally {
            commit('setEcoscoreDriversLoading', false)
        }
    },
    async loadEcoscoreTrucks({ commit }) {
        commit('setEcoscoreTrucksLoading', true)
        try {
            const startDate = moment()
                .subtract(ECOSCORE_LOOKBACK_DAYS, 'days')
                .format('YYYY-MM-DD')
            const today = moment().format('YYYY-MM-DD')
            const { data } = await httpHelper.getRecursively(
                '/ecoscore/truck-ecoscore/',
                {
                    params: {
                        limit: process.env.VUE_APP_LIMIT_RECORDS_PER_REQUEST,
                        start_date: startDate,
                        end_date: today,
                    },
                }
            )
            commit(
                'setEcoscoreTrucks',
                data.results.map(item => ({
                    id: item.truck_details.id,
                    name: item.truck_details.name,
                    dailyScores: item.daily_scores
                        .filter(
                            day => day.date >= startDate && day.date <= today
                        )
                        .slice()
                        .sort((a, b) => b.date.localeCompare(a.date)),
                }))
            )
        } catch {
            commit('setEcoscoreTrucks', [])
        } finally {
            commit('setEcoscoreTrucksLoading', false)
        }
    },
}

const mutations = {
    setEcoscoreArv(state, data) {
        state.ecoscoreArv = data
    },
    setEcoscoreArvLoading(state, data) {
        state.isEcoscoreArvLoading = data
    },
    setEcoscoreDrivers(state, data) {
        state.ecoscoreDrivers = data
    },
    setEcoscoreDriversLoading(state, data) {
        state.isEcoscoreDriversLoading = data
    },
    setEcoscoreTrucks(state, data) {
        state.ecoscoreTrucks = data
    },
    setEcoscoreTrucksLoading(state, data) {
        state.isEcoscoreTrucksLoading = data
    },
}

export default {
    namespaced: true,
    state,
    getters,
    actions,
    mutations,
}
